import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';

/**
 * Shared helpers for building the list of element types available for
 * insertion/selection, used by the floating ElementSelectorComponent and the
 * inspection-type sideboard (element type step).
 */
export class ElementTypeOptionsUtil {

    public static readonly allTypesSorted: string[] =
        ['Plate', 'Stiffener', 'FacePlate', 'Bracket', 'BeamPart', 'Cable', 'Pipe', 'PipeConnector', 'Anode'];

    /** Structure types whose drawn area can be projected front-on via the user-controlled toggle. */
    public static readonly projectionElementTypes: readonly string[] = ['Stiffener', 'Bracket', 'FacePlate'];

    /** Element types present in the currently visible regions (structure + protection), in canonical order. */
    public static getVisibleElementTypes(asset: Asset): string[] {
        const types: string[] = [];
        const protectionTypes: string[] = [];

        for (const region of asset.GetStructureVisibleRegions()) {
            // New model format exposes element types through sections.
            const structureTypes = asset.isNewModelMode
                ? region.getVisibleElementTypesFromSections()
                : region.getStructureVisibleElementsTypes();
            for (const type of structureTypes) {
                if (!types.includes(type)) {
                    types.push(type);
                }
            }
        }
        for (const region of asset.GetProtectionElementsVisibleRegions()) {
            for (const type of region.getProtectionElementsVisibleElementsTypes()) {
                if (!protectionTypes.includes(type)) {
                    protectionTypes.push(type);
                }
            }
        }
        types.push(...protectionTypes);
        types.sort((a, b) =>
            ElementTypeOptionsUtil.allTypesSorted.indexOf(a) - ElementTypeOptionsUtil.allTypesSorted.indexOf(b));
        return types;
    }

    /**
     * Structural element types (no protection types) present in the visible regions, in
     * canonical order. Used by the multi-selection Structure Type filter, which only ever
     * selects StructuralElements.
     */
    public static getVisibleStructureElementTypes(asset: Asset): string[] {
        const types: string[] = [];
        for (const region of asset?.GetStructureVisibleRegions() ?? []) {
            // Merged-mesh model: per-element visibility lives on the section groups.
            const structureTypes = asset.isNewModelMode
                ? region.getVisibleElementTypesFromSections()
                : region.getStructureVisibleElementsTypes();
            for (const type of structureTypes) {
                if (!types.includes(type)) {
                    types.push(type);
                }
            }
        }
        return ElementTypeOptionsUtil.sortTypes(types);
    }

    /** Sorts type labels by the canonical order; unknown types go last. */
    public static sortTypes(types: string[]): string[] {
        const rank = (t: string) => {
            const i = ElementTypeOptionsUtil.allTypesSorted.indexOf(t);
            return i === -1 ? Number.MAX_SAFE_INTEGER : i;
        };
        return [...types].sort((a, b) => rank(a) - rank(b));
    }

    /** Structure Type label of an element: its TypeName, falling back to the TypeId mapping. */
    public static typeNameOf(element: any): string {
        return element?.properties?.TypeName || ElementTypeOptionsUtil.mapElementTypeByTypeId(element?.TypeId);
    }

    /** True when the element's Structure Type is one of `allowedTypes`. */
    public static isTypeAllowed(element: any, allowedTypes: readonly string[]): boolean {
        const type = ElementTypeOptionsUtil.typeNameOf(element);
        return !!type && (allowedTypes ?? []).includes(type);
    }

    /** Element type name for a structural TypeId, or '' when unknown. */
    public static mapElementTypeByTypeId(typeId: ELEMENTSTYPESIDS): string {
        switch (typeId) {
            case ELEMENTSTYPESIDS.PLATE:
                return 'Plate';
            case ELEMENTSTYPESIDS.STIFFENER:
                return 'Stiffener';
            case ELEMENTSTYPESIDS.FACEPLATE:
                return 'FacePlate';
            case ELEMENTSTYPESIDS.BRACKET:
                return 'Bracket';
            case ELEMENTSTYPESIDS.BEAMPART:
                return 'BeamPart';
            case ELEMENTSTYPESIDS.CABLE:
                return 'Cable';
            case ELEMENTSTYPESIDS.PIPE:
                return 'Pipe';
            case ELEMENTSTYPESIDS.ANODE:
                return 'Anode';
            case ELEMENTSTYPESIDS.PROTECTION:
                return 'Protection';
            case ELEMENTSTYPESIDS.PAINTING:
                return 'Painting';
            default:
                return '';
        }
    }

    /**
     * Resolves the "Front-facing projection" toggle state for the current Structure-Type
     * selection.
     *
     * - The toggle is only shown when the host flow exposes it (per-flow gate), the chosen
     *   type is a projection type (Stiffener/Bracket/FacePlate), AND an element of that same
     *   type is selected in the viewer.
     * - Projection value: non-projection types always project front-on (true); projection types
     *   default to false (the user opts in via the visible toggle).
     * - When the flow disables projection, the shared value is left untouched (null).
     *
     * @param perFlowEnabled host flow exposes the projection toggle (showProjectionToggle input)
     * @param selectedType the chosen Structure Type
     * @param selectedElementMatchesType a viewer element of that same type is currently selected
     * @returns toggleVisible — whether to render the toggle;
     *          valueToApply — front-projection value to write, or null to leave it unchanged
     */
    public static resolveProjection(
        perFlowEnabled: boolean,
        selectedType: string,
        selectedElementMatchesType: boolean,
    ): { toggleVisible: boolean; valueToApply: boolean | null } {
        if (!perFlowEnabled) {
            return { toggleVisible: false, valueToApply: null };
        }
        const isProjectionType = ElementTypeOptionsUtil.projectionElementTypes.includes(selectedType);
        return {
            toggleVisible: isProjectionType && selectedElementMatchesType,
            valueToApply: !isProjectionType,
        };
    }
}
